import { useReducer, useCallback, RefObject } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import GET_SEARCH_RESULTS from '@/API/getSearchResults';
import { loadFilters } from '@/utils/filtersStorage';
import { SEOSegmentationFilterInput } from '@/__generated__/globalTypes';
import {
  getSearchResults as GetSearchResults,
  getSearchResultsVariables as GetSearchResultsVariables,
} from '@/__generated__/getSearchResults';
import { seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResults } from '@/__generated__/seoSegmentationListingPage';
import { searchReducer } from './searchResultsReducer';

export interface UseSearchResultsProps {
  initialFilters: SEOSegmentationSearchResults['filters'];
  initialPagination: SEOSegmentationSearchResults['pagination'];
  seoSegmentId: string;
  isNationalPage: boolean;
  showUserFilters: boolean;
  initialSearchResults?: {
    caregiverSearchResults?: SEOSegmentationSearchResults['caregiverSearchResults'];
    providerSearchResults?: SEOSegmentationSearchResults['providerSearchResults'];
    jobSearchResults?: SEOSegmentationSearchResults['jobSearchResults'];
    archivedJobSearchResults?: SEOSegmentationSearchResults['archivedJobSearchResults'];
  };
}

export const useSearchResults = ({
  initialFilters,
  initialPagination,
  seoSegmentId,
  isNationalPage,
  showUserFilters,
  initialSearchResults = {},
}: UseSearchResultsProps) => {
  const router = useRouter();

  // Initialize state with useReducer using our factory function
  const [state, dispatch] = useReducer(searchReducer, {
    filters: initialFilters,
    pagination: initialPagination,
    caregiverSearchResults: initialSearchResults.caregiverSearchResults ?? [],
    providerSearchResults: initialSearchResults.providerSearchResults ?? [],
    jobSearchResults: initialSearchResults.jobSearchResults ?? [],
    archivedJobSearchResults: initialSearchResults.archivedJobSearchResults ?? [],
    pageNumber: 1,
  });

  // Extract state properties for easier access
  const {
    filters,
    pagination,
    caregiverSearchResults,
    providerSearchResults,
    jobSearchResults,
    archivedJobSearchResults,
    pageNumber,
  } = state;

  // Helper function to create query variables
  const getQueryVariables = useCallback(
    (filterValues: SEOSegmentationFilterInput, page: number = pageNumber) => {
      return {
        filters: filterValues,
        pageNumber: page,
        seoSegmentId,
        ipAddress: '',
        botInfo: '',
        userGeoInfo: '',
        userAgent: '',
        hasFilters: isNationalPage || showUserFilters,
      };
    },
    [pageNumber, seoSegmentId, isNationalPage, showUserFilters]
  );

  // Initialize the GraphQL query hook
  const [getSearchResults, { loading: isLoading, called: isClientSearchResultsQuery }] =
    useLazyQuery<GetSearchResults, GetSearchResultsVariables>(GET_SEARCH_RESULTS, {
      variables: getQueryVariables(loadFilters()),
      onCompleted: (data: GetSearchResults) => {
        const searchResultData =
          data.seoSegmentationSearchResults.__typename === 'SEOSegmentationSearchResultsSuccess'
            ? data.seoSegmentationSearchResults
            : null;

        if (searchResultData) {
          dispatch({
            type: 'UPDATE_ALL_RESULTS',
            payload: {
              filters: searchResultData.filters,
              pagination: searchResultData.pagination,
              caregiverSearchResults: searchResultData.caregiverSearchResults,
              providerSearchResults: searchResultData.providerSearchResults,
              jobSearchResults: searchResultData.jobSearchResults,
              archivedJobSearchResults: searchResultData.archivedJobSearchResults,
            },
          });
        }
      },
    });

  // Handle smooth scrolling to an element
  const scrollToElement = useCallback((scrollRef?: RefObject<HTMLElement>) => {
    if (scrollRef?.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Handle page clicks
  const handlePageClick = useCallback(
    (newPage: number, scrollRef: RefObject<HTMLElement>) => {
      const filterSelected = loadFilters() || {};
      if (filters?.location) {
        filterSelected.zipcode = filters.location.zipcode;
      }

      scrollToElement(scrollRef);
      dispatch({ type: 'SET_PAGE_NUMBER', payload: newPage });

      getSearchResults({
        variables: getQueryVariables(filterSelected, newPage),
      });
    },
    [filters, getQueryVariables, getSearchResults, scrollToElement]
  );

  // Handle fetching results with filters
  const fetchResults = useCallback(
    (
      filterVals: SEOSegmentationFilterInput,
      resetPage: boolean,
      scrollRef: RefObject<HTMLElement>
    ) => {
      scrollToElement(scrollRef);

      const newPage = resetPage ? 1 : pageNumber;

      getSearchResults({
        variables: getQueryVariables(filterVals, newPage),
      });

      if (resetPage) {
        dispatch({ type: 'SET_PAGE_NUMBER', payload: 1 });
        router.push(seoSegmentId, undefined, { shallow: true });
      }
    },
    [getQueryVariables, getSearchResults, pageNumber, router, scrollToElement, seoSegmentId]
  );

  // Create a memoized setFilters function that uses dispatch
  const setFilters = useCallback((newFilters: SEOSegmentationSearchResults['filters']) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
  }, []);

  return {
    filters,
    setFilters,
    pagination,
    caregiverSearchResults,
    providerSearchResults,
    jobSearchResults,
    archivedJobSearchResults,
    pageNumber,
    isLoading,
    isClientSearchResultsQuery,
    handlePageClick,
    fetchResults,
  };
};
